/**
 * Alpha Product Slider JavaScript
 * Enhanced functionality for product cards and modals
 */

class AlphaProductSlider {
  constructor() {
    this.init();
  }

  init() {
    this.setupQuantityButtons();
    this.setupModalFunctionality();
    this.setupWishlistButtons();
    this.setupProductForms();
  }

  /**
   * Quantity Button Handlers
   */
  setupQuantityButtons() {
    document.querySelectorAll('.alpha-quantity').forEach(quantityEl => {
      const input = quantityEl.querySelector('.alpha-qty-input');
      const minusBtn = quantityEl.querySelector('.alpha-qty-minus');
      const plusBtn = quantityEl.querySelector('.alpha-qty-plus');

      if (!input || !minusBtn || !plusBtn) return;

      minusBtn.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        if (currentValue > 1) {
          input.value = currentValue - 1;
          input.dispatchEvent(new Event('change'));
        }
      });

      plusBtn.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        const maxValue = parseInt(input.max) || 999;
        if (currentValue < maxValue) {
          input.value = currentValue + 1;
          input.dispatchEvent(new Event('change'));
        }
      });

      // Validate input
      input.addEventListener('input', (e) => {
        let value = parseInt(e.target.value) || 0;
        const min = parseInt(input.min) || 0;
        const max = parseInt(input.max) || 999;
        
        if (value < min) value = min;
        if (value > max) value = max;
        
        e.target.value = value;
      });
    });
  }

  /**
   * Modal Functionality
   */
  setupModalFunctionality() {
    // Modal openers
    document.querySelectorAll('modal-opener').forEach(opener => {
      const button = opener.querySelector('button');
      if (!button) return;

      button.addEventListener('click', (e) => {
        e.preventDefault();
        const modalId = opener.getAttribute('data-modal');
        const modal = document.getElementById(modalId);
        
        if (modal) {
          this.openModal(modal);
        }
      });
    });

    // Modal close buttons
    document.querySelectorAll('.alpha-modal-close').forEach(closeBtn => {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const modal = closeBtn.closest('.alpha-quick-add-modal');
        if (modal) {
          this.closeModal(modal);
        }
      });
    });

    // Close on backdrop click
    document.querySelectorAll('.alpha-quick-add-modal').forEach(modal => {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          this.closeModal(modal);
        }
      });
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const openModal = document.querySelector('.alpha-quick-add-modal.is-open');
        if (openModal) {
          this.closeModal(openModal);
        }
      }
    });
  }

  openModal(modal) {
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    
    // Trap focus within modal
    this.trapFocus(modal);
  }

  closeModal(modal) {
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    element.addEventListener('keydown', function(e) {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            lastFocusable.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            firstFocusable.focus();
            e.preventDefault();
          }
        }
      }
    });

    firstFocusable?.focus();
  }

  /**
   * Wishlist Functionality
   */
  setupWishlistButtons() {
    document.querySelectorAll('.alpha-wishlist-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        btn.classList.toggle('is-active');
        
        // Get product handle from parent card
        const card = btn.closest('.alpha-card-wrapper');
        const productLink = card?.querySelector('.alpha-media-link');
        const productUrl = productLink?.getAttribute('href');
        
        if (productUrl) {
          const productHandle = productUrl.split('/').pop();
          this.toggleWishlist(productHandle, btn.classList.contains('is-active'));
        }

        // Visual feedback
        this.showToast(
          btn.classList.contains('is-active') 
            ? 'Added to wishlist' 
            : 'Removed from wishlist'
        );
      });
    });
  }

  toggleWishlist(productHandle, isAdding) {
    // Get current wishlist from localStorage
    let wishlist = [];
    try {
      wishlist = JSON.parse(localStorage.getItem('alpha_wishlist') || '[]');
    } catch (e) {
      console.error('Error reading wishlist:', e);
    }

    if (isAdding) {
      if (!wishlist.includes(productHandle)) {
        wishlist.push(productHandle);
      }
    } else {
      wishlist = wishlist.filter(handle => handle !== productHandle);
    }

    localStorage.setItem('alpha_wishlist', JSON.stringify(wishlist));
    
    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('wishlistUpdated', { 
      detail: { wishlist, productHandle, isAdding } 
    }));
  }

  /**
   * Product Form Handlers
   */
  setupProductForms() {
    document.querySelectorAll('.alpha-product-form form').forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        
        const formData = new FormData(form);
        const button = form.querySelector('button[type="submit"]');
        
        this.addToCart(formData, button);
      });
    });
  }

  async addToCart(formData, button) {
    if (!button) return;

    const originalText = button.innerHTML;
    button.innerHTML = '<span>Adding...</span>';
    button.disabled = true;
    button.classList.add('is-loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();
      
      // Success feedback
      button.innerHTML = '<span>✓ Added!</span>';
      setTimeout(() => {
        button.innerHTML = originalText;
        button.disabled = false;
        button.classList.remove('is-loading');
      }, 2000);

      // Update cart count
      this.updateCartCount();
      
      // Show success toast
      this.showToast(`${data.title} added to cart`);

      // Dispatch event for cart drawer
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: data }));

    } catch (error) {
      console.error('Add to cart error:', error);
      button.innerHTML = '<span>Error - Try again</span>';
      setTimeout(() => {
        button.innerHTML = originalText;
        button.disabled = false;
        button.classList.remove('is-loading');
      }, 2000);

      this.showToast('Failed to add to cart', 'error');
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count badge
      const cartCountElements = document.querySelectorAll('.cart-count-bubble span');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }

  /**
   * Toast Notification
   */
  showToast(message, type = 'success') {
    // Remove existing toasts
    document.querySelectorAll('.alpha-toast').forEach(toast => toast.remove());

    const toast = document.createElement('div');
    toast.className = `alpha-toast alpha-toast--${type}`;
    toast.innerHTML = `
      <div class="alpha-toast-content">
        <span class="alpha-toast-icon">
          ${type === 'success' ? '✓' : '✕'}
        </span>
        <span class="alpha-toast-message">${message}</span>
      </div>
    `;

    // Add toast styles if not present
    if (!document.getElementById('alpha-toast-styles')) {
      const styles = document.createElement('style');
      styles.id = 'alpha-toast-styles';
      styles.textContent = `
        .alpha-toast {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 9999;
          background: white;
          padding: 16px 24px;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          animation: toastSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        @keyframes toastSlideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .alpha-toast-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .alpha-toast-icon {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          color: white;
        }
        
        .alpha-toast--success .alpha-toast-icon {
          background: rgb(34, 197, 94);
        }
        
        .alpha-toast--error .alpha-toast-icon {
          background: rgb(239, 68, 68);
        }
        
        .alpha-toast-message {
          font-size: 14px;
          font-weight: 500;
          color: rgb(17, 24, 39);
        }
        
        @media (max-width: 749px) {
          .alpha-toast {
            bottom: 16px;
            right: 16px;
            left: 16px;
            width: auto;
          }
        }
      `;
      document.head.appendChild(styles);
    }

    document.body.appendChild(toast);

    // Auto remove after 3 seconds
    setTimeout(() => {
      toast.style.animation = 'toastSlideOut 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  /**
   * Load Wishlist State on Init
   */
  loadWishlistState() {
    try {
      const wishlist = JSON.parse(localStorage.getItem('alpha_wishlist') || '[]');
      
      document.querySelectorAll('.alpha-card-wrapper').forEach(card => {
        const productLink = card.querySelector('.alpha-media-link');
        const productUrl = productLink?.getAttribute('href');
        
        if (productUrl) {
          const productHandle = productUrl.split('/').pop();
          const wishlistBtn = card.querySelector('.alpha-wishlist-btn');
          
          if (wishlist.includes(productHandle) && wishlistBtn) {
            wishlistBtn.classList.add('is-active');
          }
        }
      });
    } catch (e) {
      console.error('Error loading wishlist state:', e);
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const slider = new AlphaProductSlider();
    slider.loadWishlistState();
  });
} else {
  const slider = new AlphaProductSlider();
  slider.loadWishlistState();
}

// Export for use in other scripts
window.AlphaProductSlider = AlphaProductSlider;